/**
 * Funnel Context - State Management for the Onboarding Flow
 * 
 * This context provides:
 * - Global state for all funnel answers
 * - Navigation between steps
 * - Progress tracking
 * - Local storage persistence
 * - Service type branching logic
 * 
 * Uses useReducer for predictable state updates and easy debugging.
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  FunnelState,
  FunnelAction,
  ServiceType,
} from '../types/funnel.types';
import { TOTAL_STEPS, getQuestionFlow } from '../data/questions';

// ============================================
// CONSTANTS
// ============================================

// Key for localStorage persistence
const STORAGE_KEY = 'happythumbss-funnel-progress';

// Initial state when starting fresh
const initialState: FunnelState = {
  currentStep: 1,
  totalSteps: TOTAL_STEPS,
  answers: {},
  serviceType: undefined,
  progress: 0,
  isComplete: false,
  startedAt: new Date().toISOString(),
};

// ============================================
// REDUCER
// ============================================

/**
 * Funnel reducer handles all state transitions
 * Each action type corresponds to a specific state change
 */
function funnelReducer(state: FunnelState, action: FunnelAction): FunnelState {
  switch (action.type) {
    // Save an answer for a specific question
    case 'SET_ANSWER':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: action.value,
        },
      };

    // Set the service type (used for branching logic)
    case 'SET_SERVICE_TYPE':
      return {
        ...state,
        serviceType: action.serviceType,
      };

    // Move to the next step
    case 'NEXT_STEP': {
      const nextStep = Math.min(state.currentStep + 1, state.totalSteps);
      const progress = calculateProgress(nextStep, state.totalSteps);
      return {
        ...state,
        currentStep: nextStep,
        progress,
      };
    }

    // Move to the previous step
    case 'PREVIOUS_STEP': {
      const prevStep = Math.max(state.currentStep - 1, 1);
      const progress = calculateProgress(prevStep, state.totalSteps);
      return {
        ...state,
        currentStep: prevStep,
        progress,
      };
    }

    // Jump to a specific step
    case 'GO_TO_STEP': {
      const step = Math.max(1, Math.min(action.step, state.totalSteps));
      const progress = calculateProgress(step, state.totalSteps);
      return {
        ...state,
        currentStep: step,
        progress,
      };
    }

    // Update progress percentage
    case 'SET_PROGRESS':
      return {
        ...state,
        progress: action.progress,
      };

    // Mark the funnel as complete
    case 'COMPLETE_FUNNEL':
      return {
        ...state,
        isComplete: true,
        completedAt: new Date().toISOString(),
        progress: 100,
      };

    // Reset to initial state
    case 'RESET_FUNNEL':
      return {
        ...initialState,
        startedAt: new Date().toISOString(),
      };

    // Restore state from localStorage
    case 'RESTORE_STATE':
      return {
        ...action.state,
      };

    default:
      return state;
  }
}

/**
 * Calculate progress percentage based on current step
 * Subtracts 1 from current step because we start at step 1
 */
function calculateProgress(currentStep: number, totalSteps: number): number {
  // Progress is 0% at step 1, 100% at final step
  return Math.round(((currentStep - 1) / (totalSteps - 1)) * 100);
}

// ============================================
// CONTEXT TYPES
// ============================================

interface FunnelContextValue {
  // State
  state: FunnelState;
  
  // Answer management
  setAnswer: (questionId: string, value: unknown) => void;
  getAnswer: <T>(questionId: string) => T | undefined;
  
  // Navigation
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  
  // Service type
  setServiceType: (type: ServiceType) => void;
  
  // Completion
  completeFunnel: () => void;
  resetFunnel: () => void;
  
  // Question flow
  getQuestionForCurrentStep: () => ReturnType<typeof getQuestionFlow>[number] | undefined;
  getTotalQuestionsForFlow: () => number;
}

// Create context with undefined default (will be set by provider)
const FunnelContext = createContext<FunnelContextValue | undefined>(undefined);

// ============================================
// PROVIDER COMPONENT
// ============================================

interface FunnelProviderProps {
  children: ReactNode;
}

/**
 * FunnelProvider wraps the application and provides funnel state
 * Handles localStorage persistence on state changes
 */
export function FunnelProvider({ children }: FunnelProviderProps) {
  // Initialize reducer with initial state
  const [state, dispatch] = useReducer(funnelReducer, initialState);

  // ============================================
  // PERSISTENCE
  // ============================================

  // Load saved state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState) as FunnelState;
        // Only restore if not already completed
        if (!parsed.isComplete) {
          dispatch({ type: 'RESTORE_STATE', state: parsed });
        }
      }
    } catch (error) {
      // If there's an error parsing, start fresh
      console.warn('Failed to restore funnel state:', error);
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save funnel state:', error);
    }
  }, [state]);

  // ============================================
  // ACTION HANDLERS
  // ============================================

  /**
   * Set an answer for a specific question
   */
  const setAnswer = useCallback((questionId: string, value: unknown) => {
    dispatch({ type: 'SET_ANSWER', questionId, value });
  }, []);

  /**
   * Get an answer by question ID (type-safe)
   */
  const getAnswer = useCallback(<T,>(questionId: string): T | undefined => {
    return state.answers[questionId] as T | undefined;
  }, [state.answers]);

  /**
   * Move to the next step
   */
  const nextStep = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' });
  }, []);

  /**
   * Move to the previous step
   */
  const previousStep = useCallback(() => {
    dispatch({ type: 'PREVIOUS_STEP' });
  }, []);

  /**
   * Jump to a specific step
   */
  const goToStep = useCallback((step: number) => {
    dispatch({ type: 'GO_TO_STEP', step });
  }, []);

  /**
   * Set the service type for conditional branching
   */
  const setServiceType = useCallback((type: ServiceType) => {
    dispatch({ type: 'SET_SERVICE_TYPE', serviceType: type });
  }, []);

  /**
   * Mark the funnel as complete
   * Also clears localStorage after successful submission
   */
  const completeFunnel = useCallback(() => {
    dispatch({ type: 'COMPLETE_FUNNEL' });
    // Clear saved progress after successful submission
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Reset the funnel to start over
   */
  const resetFunnel = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    dispatch({ type: 'RESET_FUNNEL' });
  }, []);

  /**
   * Get the question for the current step
   * Handles conditional logic based on service type
   */
  const getQuestionForCurrentStep = useCallback(() => {
    const flow = getQuestionFlow(state.serviceType);
    // Step is 1-indexed, array is 0-indexed
    return flow[state.currentStep - 1];
  }, [state.currentStep, state.serviceType]);

  /**
   * Get total questions for current flow
   * Used for accurate progress calculation
   */
  const getTotalQuestionsForFlow = useCallback(() => {
    const flow = getQuestionFlow(state.serviceType);
    // Add 1 for the thank you page
    return flow.length + 1;
  }, [state.serviceType]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const contextValue: FunnelContextValue = {
    state,
    setAnswer,
    getAnswer,
    nextStep,
    previousStep,
    goToStep,
    setServiceType,
    completeFunnel,
    resetFunnel,
    getQuestionForCurrentStep,
    getTotalQuestionsForFlow,
  };

  return (
    <FunnelContext.Provider value={contextValue}>
      {children}
    </FunnelContext.Provider>
  );
}

// ============================================
// CUSTOM HOOK
// ============================================

/**
 * Hook to access the funnel context
 * Throws an error if used outside of FunnelProvider
 */
export function useFunnel(): FunnelContextValue {
  const context = useContext(FunnelContext);
  
  if (context === undefined) {
    throw new Error('useFunnel must be used within a FunnelProvider');
  }
  
  return context;
}

// Export the context for advanced use cases
export { FunnelContext };
